import { showToast } from "@/components/feedback";
import { Badge, InteractiveCard, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  CouponCard as CouponCardArt,
  couponCornerRadiusUnits,
  sharedCouponFooterStyle,
  type CouponFooterStyle,
} from "@/components/home/CouponCards";
import { RAIL_COLUMN, RailArrow, SectionRail } from "@/components/home/SectionRail";
import { ProgressiveImage } from "@/components/progressive-image";
import {
  COUPON_WIDTH_RATIO,
  RAIL_FADE_REM,
  STOREFRONT_COUPON_ART_ASPECT,
  STOREFRONT_ROUNDED_PX,
  STOREFRONT_SECTION_LABELS,
  couponArtWidthRatio,
  couponCardWidthRem,
  type StorefrontCouponArt,
  type StorefrontCouponDisplay,
  type StorefrontSection,
  type StorefrontSizeRounded,
} from "@/domain/storefront-appearance";
import {
  formatCouponAvailabilityHint,
  formatCouponAvailabilityMessage,
  isCouponAvailableNow,
} from "@/lib/coupon-availability";
import { useExposedCoupons } from "@/hooks/use-exposed-coupons";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { getMerchantId } from "@/lib/company-storage";
import { exposedCouponBenefit } from "@/lib/exposed-coupon-benefit";
import { formatBRL } from "@/lib/format-brl";
import { savePendingCoupon } from "@/lib/pending-coupon";
import { ExposedCoupon } from "@/types/external/exposed-coupon";
import { useEffect, useMemo, useRef, useState } from "react";

/** How many identical tracks sit in the rail so wrapping is invisible. */
const LOOP_COPIES = 3;

/**
 * Repeat ceiling. Stops the pathological case — one coupon, narrowest piece,
 * wide screen — from becoming a strip of dozens of SVGs. At the ceiling the
 * loop is imperfect, but the user has already scrolled more than a viewport.
 */
export const COUPON_LOOP_MAX_REPEATS = 12;

export type CouponLoopInput = {
  /** How many coupons the merchant exposed. */
  couponCount: number;
  /** Width of one slide, in px. */
  slideWidth: number;
  /** Start of one slide to start of the next, in px: width plus gap. */
  pitch: number;
  /** VISIBLE rail width in px — the scroller's `clientWidth`. */
  viewport: number;
};

/**
 * How many times the coupon list repeats INSIDE each loop copy.
 *
 * Lives HERE with the scroller, not in appearance domain: `rail-metrics`
 * answers piece size; this answers how many DOM copies the scroller needs.
 * Pure function does not make it domain.
 *
 * Infinite carousel is three identical copies: start in the middle; when the
 * centered slide lands on an outer copy, recentre the middle equivalent. That
 * only works if an edge slide can be centered — with few coupons the track was
 * narrower than the viewport and wrap never fired. Condition:
 * `copy * pitch >= (viewport + slide) / 2 + gap`, plus one `pitch` of slack.
 * Returns 1 when unmeasurable (zero viewport in tests / before layout).
 */
export function couponLoopRepeats({
  couponCount,
  slideWidth,
  pitch,
  viewport,
}: CouponLoopInput): number {
  if (couponCount <= 0 || pitch <= 0 || viewport <= 0) return 1;

  const gap = Math.max(0, pitch - slideWidth);
  const needed = (viewport + slideWidth) / 2 + gap + pitch;
  const repeats = Math.ceil(needed / (couponCount * pitch));

  return Math.min(COUPON_LOOP_MAX_REPEATS, Math.max(1, repeats));
}

function slideItems(scroller: HTMLElement): HTMLElement[] {
  return Array.from(scroller.querySelectorAll<HTMLElement>(":scope > ul > li"));
}

/** `scrollLeft` that places `item` dead-center in the scroller viewport. */
function scrollLeftToCenter(scroller: HTMLElement, item: HTMLElement): number {
  return item.offsetLeft + item.offsetWidth / 2 - scroller.clientWidth / 2;
}

function nearestSlideIndex(scroller: HTMLElement): number {
  const items = slideItems(scroller);
  const center = scroller.scrollLeft + scroller.clientWidth / 2;
  let best = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const dist = Math.abs(item.offsetLeft + item.offsetWidth / 2 - center);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

function setSnapEnabled(scroller: HTMLElement, enabled: boolean) {
  scroller.style.scrollSnapType = enabled ? "" : "none";
}

function centerItem(scroller: HTMLElement, item: HTMLElement, behavior: ScrollBehavior) {
  const left = scrollLeftToCenter(scroller, item);
  if (behavior !== "smooth") {
    scroller.scrollLeft = left;
    return;
  }
  if (typeof scroller.scrollTo === "function") {
    scroller.scrollTo({ left, behavior: "smooth" });
    return;
  }
  scroller.scrollLeft = left;
}

/**
 * Horizontal snap-scroll rail for coupons the merchant exposed to the
 * storefront (portal's expose-on-menu toggle). Rendered above home categories;
 * hidden when there are no exposed coupons.
 *
 * The track is three copies of the list. Start on the middle copy; when the
 * user lands on a clone, jump to the same card in the middle so the rail never
 * hits a hard end. Each card snaps to center; arrows advance one card.
 *
 * Each copy repeats the list `repeats` times — MEASURED, not hard-coded. Three
 * copies of a short list (often one coupon) yield a track narrower than the
 * screen and wrap never fires. {@link couponLoopRepeats} computes repeats; this
 * component only measures the ruler the DOM knows after rem/padding/max-width.
 *
 * Section size becomes piece WIDTH; `couponArt` becomes ASPECT — separate on
 * purpose (upload export requirement). Section `imageVisible` is ignored: here
 * the section IS the image. Card mode uses the same SVG as the portal preview.
 */
export function ExposedCouponsCarousel({ section }: { section: StorefrontSection }) {
  const { rounded: corners } = section;
  const coupons = useExposedCoupons();
  const scrollRef = useRef<HTMLDivElement>(null);
  const fills = useMediaQuery("(min-width: 768px)");
  const [repeats, setRepeats] = useState(1);
  /**
   * RENDERED slide width. Same measure that sizes the loop — first item
   * `offsetWidth` — and feeds the coupon corner (5px on screen depends on size).
   */
  const [slideWidthPx, setSlideWidthPx] = useState(0);
  /**
   * Slide width uses different rulers by mode on purpose: PHOTO is a banner
   * (fraction of the column); CARD is a small card (step height × aspect). All
   * three sizes apply in both — what "large" means differs.
   */
  const slideWidth =
    section.couponDisplay === "card"
      ? `${couponCardWidthRem(section.size)}rem`
      : `${
          couponArtWidthRatio(
            section.couponArt,
            COUPON_WIDTH_RATIO[fills ? "desktop" : "mobile"][section.size],
          ) * 100
        }%`;

  /** Coupons per copy — the repeated list, not the raw list. */
  const count = coupons.length * repeats;

  /**
   * Footer for ALL coupons, decided once. From the real list, not the repeated
   * one — copies are the same coupons again and would only raise cost.
   */
  const footerStyle = useMemo(
    () =>
      sharedCouponFooterStyle(
        coupons.map((coupon) => ({
          code: coupon.code,
          description: formatMinPurchaseLabel(coupon),
        })),
      ),
    [coupons],
  );

  /**
   * `key` carries repeat and position, not only the code: with a repeated list
   * inside a copy, `${copy}-${code}` collides and React reuses the wrong `li`.
   */
  const looped = useMemo(
    () =>
      Array.from({ length: LOOP_COPIES }, (_, copy) =>
        Array.from({ length: repeats }, (_, repeat) =>
          coupons.map((coupon, position) => ({
            coupon,
            copy,
            repeat,
            key: `${copy}-${repeat}-${position}-${coupon.code}`,
          })),
        ).flat(),
      ).flat(),
    [coupons, repeats],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || count === 0) return;

    /**
     * Measure the rail and adjust repeats. `pitch` comes from distance between
     * two slides so gap enters as CSS produced it, without rebuilding rem.
     */
    const measureRepeats = () => {
      const items = slideItems(el);
      if (items.length < 2) return;
      const width = items[0].offsetWidth;
      const next = couponLoopRepeats({
        couponCount: coupons.length,
        slideWidth: width,
        pitch: items[1].offsetLeft - items[0].offsetLeft,
        viewport: el.clientWidth,
      });
      setRepeats((current) => (current === next ? current : next));
      setSlideWidthPx((current) => (current === width ? current : width));
    };

    const jumpToMiddle = () => {
      const items = slideItems(el);
      const firstOfMiddle = items[count];
      if (firstOfMiddle) {
        setSnapEnabled(el, false);
        centerItem(el, firstOfMiddle, "auto");
        setSnapEnabled(el, true);
      }
    };

    const wrapIfNeeded = () => {
      const items = slideItems(el);
      if (items.length < count * LOOP_COPIES) return;
      const index = nearestSlideIndex(el);
      if (index < count) {
        setSnapEnabled(el, false);
        centerItem(el, items[index + count], "auto");
        setSnapEnabled(el, true);
      } else if (index >= count * 2) {
        setSnapEnabled(el, false);
        centerItem(el, items[index - count], "auto");
        setSnapEnabled(el, true);
      }
    };

    const recenterCurrent = () => {
      const items = slideItems(el);
      const item = items[nearestSlideIndex(el)];
      if (!item) return;
      setSnapEnabled(el, false);
      centerItem(el, item, "auto");
      setSnapEnabled(el, true);
      wrapIfNeeded();
    };

    let scrollEndTimer = 0;
    const onScroll = () => {
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(wrapIfNeeded, 80);
    };

    measureRepeats();
    jumpToMiddle();
    const raf = requestAnimationFrame(() => {
      measureRepeats();
      jumpToMiddle();
    });
    el.addEventListener("scrollend", wrapIfNeeded);
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(() => {
      measureRepeats();
      recenterCurrent();
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(scrollEndTimer);
      el.removeEventListener("scrollend", wrapIfNeeded);
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [coupons, count]);

  function scrollByDirection(direction: -1 | 1) {
    const el = scrollRef.current;
    if (!el) return;
    const items = slideItems(el);
    const next = items[nearestSlideIndex(el) + direction];
    if (!next) return;
    centerItem(el, next, "smooth");
  }

  if (coupons.length === 0) return null;

  const fade = `${RAIL_FADE_REM}rem`;
  const edgeFadeMask = `linear-gradient(to right, transparent 0, black ${fade}, black calc(100% - ${fade}), transparent 100%)`;

  return (
    <SectionRail
      label={STOREFRONT_SECTION_LABELS.coupons.label}
      titleCard={section.titleCard}
    >
      <div className={`relative ${RAIL_COLUMN}`}>
        <div
          ref={scrollRef}
          data-coupon-rail
          className="overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitMaskImage: edgeFadeMask,
            maskImage: edgeFadeMask,
          }}
        >
          <ul className="flex gap-3 pb-1">
            {looped.map(({ coupon, copy, repeat, key }) => (
              <li
                key={key}
                className="snap-center shrink-0"
                style={{ width: slideWidth }}
                /**
                 * Only one slide per coupon is REAL for screen readers: first
                 * repeat of the middle copy. Others are duplicates.
                 */
                aria-hidden={copy !== 1 || repeat !== 0}
              >
                <CouponCard
                  coupon={coupon}
                  art={section.couponArt}
                  display={section.couponDisplay}
                  rounded={corners}
                  slideWidthPx={slideWidthPx}
                  footerStyle={footerStyle}
                />
              </li>
            ))}
          </ul>
        </div>

        <RailArrow direction="prev" onClick={() => scrollByDirection(-1)} />
        <RailArrow direction="next" onClick={() => scrollByDirection(1)} />
      </div>
    </SectionRail>
  );
}

/** `true` when the coupon can no longer be redeemed — EXHAUSTED status or no uses left. */
function isCouponExhausted(coupon: ExposedCoupon): boolean {
  return coupon.status === "EXHAUSTED" || coupon.remainingUses <= 0;
}

/** Coupon floor as the customer reads it: a value, or its absence. */
function formatMinPurchaseLabel(coupon: ExposedCoupon): string {
  return coupon.minPurchaseValue > 0
    ? `Acima de ${formatBRL(coupon.minPurchaseValue)}`
    : "Sem mínimo";
}

interface CouponProps {
  coupon: ExposedCoupon,
  art: StorefrontCouponArt,
  display: StorefrontCouponDisplay,
  /**
   * Corner of the art FRAME. Photo mode only: the built coupon's shape lives in
   * the `viewBox` with seam and notches; an outer radius would deform it.
   */
  rounded: StorefrontSizeRounded,
  /** Drawn piece width — SVG corner comes from it. Zero before measure. */
  slideWidthPx: number,
  /** Footer the ROW decided; this coupon does not pick its own. */
  footerStyle: CouponFooterStyle,
}

function CouponCard({
  coupon,
  art,
  display,
  rounded,
  slideWidthPx,
  footerStyle,
}: CouponProps) {
  const { colors } = useStorefrontAppearance();
  const exhausted = isCouponExhausted(coupon);
  const availableNow = isCouponAvailableNow(coupon.availability);
  const outOfWindow = !exhausted && !availableNow;
  const subdued = exhausted || outOfWindow;
  const availabilityHint = formatCouponAvailabilityHint(coupon.availability);
  const benefit = exposedCouponBenefit(coupon);
  const showPhoto = display === "photo" && Boolean(coupon.imageUrl);
  const aspect = STOREFRONT_COUPON_ART_ASPECT[art];

  async function handleTap() {
    if (exhausted) return;
    if (outOfWindow) {
      showToast.error(formatCouponAvailabilityMessage(coupon.availability));
      return;
    }
    const merchantId = getMerchantId();
    if (!merchantId || !savePendingCoupon(coupon.code, merchantId)) {
      showToast.error("Não foi possível guardar o cupom");
      return;
    }
    showToast.success(`Cupom ${coupon.code}, aplicado com sucesso!`);
    try {
      await navigator.clipboard.writeText(coupon.code);
    } catch {
    }
  }

  return (
    <div className="flex w-full flex-col items-center gap-1">
      <InteractiveCard
        variant={showPhoto ? "elevated" : "plain"}
        padding="none"
        onClick={handleTap}
        disabled={exhausted}
        dimWhenDisabled={false}
        aria-label={
          exhausted
            ? `Cupom ${coupon.code}, esgotado`
            : outOfWindow
              ? `Cupom ${coupon.code}, indisponível agora. ${formatCouponAvailabilityMessage(coupon.availability)}`
              : `Aplicar cupom ${coupon.code} no pagamento — ${benefit.phrase}`
        }
        style={
          showPhoto
            ? {
                aspectRatio: aspect,
                borderRadius: `${STOREFRONT_ROUNDED_PX[rounded]}px`,
              }
            : undefined
        }
        className={cn("relative w-full", showPhoto && "overflow-hidden")}
      >
        {showPhoto ? (
          <ProgressiveImage
            src={coupon.imageUrl!}
            alt={`Cupom ${coupon.code}`}
            width={455}
            height={Math.round(455 / aspect)}
            sizes="85vw"
            className={cn("h-full w-full object-cover", subdued && "opacity-60 grayscale")}
          />
        ) : (
          <span className={cn("block", subdued && "opacity-45 grayscale")}>
            <CouponCardArt
              title={benefit.title}
              discount={benefit.value}
              code={coupon.code}
              description={formatMinPurchaseLabel(coupon)}
              cornerRadius={couponCornerRadiusUnits(slideWidthPx)}
              footerStyle={footerStyle}
              accentColor={colors.primary}
              iconBackgroundColor={tinted(colors.secondary)}
              iconColor={colors.secondary}
              backgroundColor={colors.cardBackground}
              textColor={colors.cardText}
              mutedTextColor={colors.cardText}
              borderColor={colors.border}
            />
          </span>
        )}

        {exhausted && (
          <Badge
            variant="solid"
            tone="inverse"
            size={showPhoto ? "sm" : "2xs"}
            uppercase
            className="absolute right-2 top-2 z-raised"
          >
            Esgotado
          </Badge>
        )}
      </InteractiveCard>

      {outOfWindow && availabilityHint && (
        <Badge
          variant="solid"
          tone="inverse"
          size="xs"
          uppercase
          className="max-w-full whitespace-normal break-words text-center"
        >
          {availabilityHint}
        </Badge>
      )}
      {!subdued && availabilityHint && (
        <Text as="span" size="2xs" leading="tight" tone="subtle" align="center" className="block max-w-full break-words">
          {availabilityHint}
        </Text>
      )}
    </div>
  );
}

/** The live store's `bg-secondary/10` — a wash of the role, not a solid slab. */
function tinted(hex: string, alpha = 0.1): string {
  const raw = hex.replace("#", "").trim();
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : raw;
  if (full.length !== 6) return hex;
  const red = Number.parseInt(full.slice(0, 2), 16);
  const green = Number.parseInt(full.slice(2, 4), 16);
  const blue = Number.parseInt(full.slice(4, 6), 16);
  if ([red, green, blue].some((channel) => Number.isNaN(channel))) return hex;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
